import moment from "moment";
import {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import MainActions from "@/components/MainActions";
import Breadcrumbs from "@/components/Breadcrumbs";
import Tabs from "@/components/Tabs";
import VLFCard from "@/components/Card";
import EmptyData from "@/components/EmptyData";
import ViewField from "@/components/Input/ViewField";
import VLFDatePicker from "@/components/Input/DatePicker";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";
import ChartContainer from "@/components/ChartContainer";
import PlayerStatsContainer from "@/components/PlayerStatsContainer";
import PlayerRadarChart from "@/components/PlayerRadarChart";
import Button from "@mui/material/Button";
import VLFModal from "@/components/Modal";
import HandleCareerModal from "@/pages/Scouting/Players/Player/handleCareer";
import AttachmentsContainer from "@/components/AttachmentsContainer";
import HandlePhysicHeightInfo from "@/pages/Player/Modals/handlePhysicHeightInfo";
import HandlePhysicWeightInfo from "@/pages/Player/Modals/handlePhysicWeightInfo";
import HandlePHVInfo from "@/pages/Player/Modals/handlePHVInfo";
import HandleMeasurementInfo from "@/pages/Player/Modals/handleMeasurementInfo";
import HandleContactModal from "@/pages/Player/Modals/handleContact";
import HandleAgentModal from "@/pages/Player/Modals/handleAgent";
import HandleMovementModal from "@/pages/Player/Modals/handleMovement";
import ValuationsPage from "@/pages/Valuations";
import NotesSection from "@/components/NotesSection";
import MedicalRecordPage from "@/pages/Medical/Record";
import MatchReportsPage from "@/pages/MatchReports";
import GPSContainer from "@/components/GPSContainer";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

import {AttachmentAPI} from "@/api/attachment";
import {GpsAPI} from "@/api/gps";
import {PlayerAPI} from "@/api/player";
import {PlayerValuationAPI} from "@/api/player_valuation";
import {MatchReportAPI} from "@/api/match_report";
import {PlaylistAPI} from "@/api/playlist";
import {StatAPI} from "@/api/stat";

import {setPlayerTab} from "@/actions";

import {
	dateFormat,
	playerSemaphoreOptions,
	statusOptions,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {getAttachment, isCompetitiveActivity} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";

import "./index.scss";
import "../Manager/Match/index.scss";
import {useTranslation} from "react-i18next";
import {testTypes} from "../../utils/constants";

const MATCH_STATS = {
	goalsConceded: "Goal subiti",
	playedMinutes: "Minuti",
	matches: "Partite",
};
const matchTypes = ["Amichevole", "Campionato", "Torneo"];

function PlayerPage(props) {
	const {handleNotification} = props;

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const seasonId = useSelector((state) => state?.season?.id);
	const user = useSelector((state) => state?.user);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
	});

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [playerData, setPlayerData] = useState({});

	const [openCareerDetail, setOpenCareerDetail] = useState(false);
	const [openMovement, setOpenMovement] = useState(false);
	const [openContact, setOpenContact] = useState(false);
	const [openAgents, setOpenAgents] = useState(false);
	const [openHandleAttachmentConfirm, setOpenHandleAttachmentConfirm] =
		useState(false);
	const [openPhysicInfoWeight, setOpenPhysicInfoWeight] = useState(false);
	const [openPhysicHeightInfo, setOpenPhysicHeightInfo] = useState(false);
	const [openPHVInfo, setOpenPHVInfo] = useState(false);
	const [openMeasurementInfo, setOpenMeasurementInfo] = useState(false);

	const [initialData, setInitialData] = useState();
	const [idAttachment, setIdAttachment] = useState();
	const [attachments, setAttachments] = useState([]);

	const [valuations, setValuations] = useState([]);
	const [valuationsData, setValuationsData] = useState([]);
	const [playerMovements, setPlayerMovements] = useState([]);
	const [playerNotes, setPlayerNotes] = useState([]);
	const [playerGPSData, setPlayerGPSData] = useState({});
	const [quickStats, setQuickStats] = useState({});
	const [detailedQuickStats, setDetailedQuickStats] = useState([]);
	const [playlists, setPlaylists] = useState([]);
	const [matchReports, setMatchReports] = useState([]);

	// ------------------------
	const getValuations = async () => {
		try {
			const response = await PlayerValuationAPI.playerList(id, {seasonId});
			setValuations(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getMatchReports = async () => {
		try {
			const response = await MatchReportAPI.list({playerId: id, seasonId});
			setMatchReports(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getPlayerMovements = async () => {
		try {
			let response = await PlayerAPI.getPlayerMovements(id);
			response = response.map((movement, index) => {
				return checkMovement(response, movement, index);
			});
			setPlayerMovements(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getPlayerAttachments = async () => {
		try {
			const response = await AttachmentAPI.list("player", id);
			setAttachments(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const handleDownload = async (id) => {
		setLoading(true);
		try {
			await getAttachment(id, attachments);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteAttachment = async () => {
		setLoading(true);
		try {
			await AttachmentAPI.delete(idAttachment);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleAttachmentConfirm(false);
			getPlayerAttachments();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getQuickStats = async () => {
		try {
			const response = await PlayerAPI.getQuickStats(id, {
				grouped: true,
				seasonId,
			});
			const detailedResponse = await PlayerAPI.getDetailedQuickStats(id, {
				seasonId,
			});
			setQuickStats(response);
			setDetailedQuickStats(detailedResponse);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getPlayerNotes = async () => {
		try {
			const response = await PlayerAPI.getPlayerNotes(id, {seasonId});
			setPlayerNotes(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getPlayerGPS = async () => {
		try {
			const response = await GpsAPI.playerList(id, {seasonId});
			const GPSData = {};
			response.forEach((g) => {
				const currentMatchId = g.matchId;
				if (!GPSData[currentMatchId]) {
					GPSData[currentMatchId] = [];
				}

				GPSData[currentMatchId].push(g);
			});

			Object.keys(GPSData).forEach((key) => {
				GPSData[key].sort((a, b) => {
					const dateA = new Date(a.match.startDate);
					const dateB = new Date(b.match.startDate);
					return dateA - dateB;
				});
			});

			setPlayerGPSData(GPSData);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};
	// ------------------------

	const checkMovement = (playerMovements, m, i) => {
		if (!m.club) {
			for (let i2 = i - 1; i2 >= 0; i2--) {
				if (playerMovements[i2]?.club) {
					m.club = playerMovements[i2]?.club;
					break;
				}
			}
		}

		if (!m.membershipDate) {
			for (let i2 = i - 1; i2 >= 0; i2--) {
				if (playerMovements[i2]?.membershipDate) {
					m.membershipDate = playerMovements[i2]?.membershipDate;
					break;
				}
			}
		}

		if (!m.movementType?.status) {
			for (let i2 = i - 1; i2 >= -1; i2--) {
				if (playerMovements[i2]?.movementType?.status) {
					m.movementType.status = playerMovements[i2]?.movementType?.status;
					break;
				}
			}
		}

		return m;
	};

	const getPlayer = async () => {
		setLoading(true);
		try {
			const response = await PlayerAPI.getById(id);
			response.hasStats = isCompetitiveActivity(
				categories,
				response.playerCategoryName,
			);

			response.career = response.career?.map((c) => {
				if (c.id?.startsWith("00000000")) {
					c.readable = true;
				}
				return c;
			});

			// controllo eventuali righe doppie per stagione-squadra-categoria
			const seen = new Set();
			response.career = response.career.filter((obj) => {
				const key = `${obj.season?.name}-${obj.club?.name}-${obj.playerCategory?.name}`;
				if (seen.has(key)) {
					return false;
				}
				seen.add(key);
				return true;
			});
			setPlayerData(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (categories?.length) {
			getPlayer();
		}
	}, [categories]);

	useEffect(() => {
		if (valuations?.length) {
			const promises = valuations.map((v) => {
				return PlayerValuationAPI.getById(v.id);
			});

			Promise.all(promises).then((responses) => {
				setValuationsData(responses);
			});
		}
	}, [valuations]);

	const deletePlayer = async () => {
		setLoading(true);
		try {
			await PlayerAPI.delete(playerData?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/players");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const mainConfiguration = [
		{
			value: playerData?.dateOfBirth
				? moment(playerData?.dateOfBirth).format(dateFormat)
				: null,
			label: t("birthdate"),
			size: "col-6",
		},
		{
			value: playerData?.placeOfBirth,
			label: "Luogo di nascita",
			size: "col-6",
		},
		{
			value: playerData?.address,
			label: t("address"),
			size: "col-6",
		},
		{
			value: playerData?.nationality,
			label: t("nationality"),
			size: "col-6",
		},
		{
			value: playerData?.email,
			label: "Email",
			size: "col-6",
		},
		{
			value: playerData?.telephone,
			label: t("phone"),
			size: "col-6",
		},
	];

	if (playerData?.status !== "Interno") {
		mainConfiguration.push({
			value: (
				<div className="d-flex align-items-center justify-content-between w-100">
					<div className="player-status">
						{
							statusOptions?.find((so) => so.value === playerData?.status)
								?.label
						}
					</div>
				</div>
			),
			label: "Status",
			size: "col-6",
		});
	}

	const teamConfiguration = [];

	if (playerData?.goalkeeper) {
		teamConfiguration.push({
			value: playerData?.favoriteHand,
			label: t("favorite_hand"),
			size: "col-6 col-lg-4",
		});
	} else {
		teamConfiguration.push({
			value: playerData?.primaryRole,
			label: t("role"),
			size: "col-6 col-lg-4",
		});
	}

	teamConfiguration.push(
		{
			value: playerData?.favoriteFoot,
			label: t("favorite_foot"),
			size: "col-6 col-lg-4",
		},
		{
			value: playerData?.fatherHeight ? playerData?.fatherHeight + " cm" : null,
			label: t("father_height"),
			size: "col-6 col-lg-4",
		},
		{
			value: playerData?.motherHeight ? playerData?.motherHeight + " cm" : null,
			label: t("mother_height"),
			size: "col-6 col-lg-4",
		},
		{
			value: playerData?.boardingSchool ? (
				<div className="cell on" />
			) : (
				<div className="cell off" />
			),
			label: t("boarding_school"),
			size: "col-6 col-lg-4",
		},
	);

	if (user.roleName === "Admin") {
		teamConfiguration.push({
			value: playerData?.semaphore
				? playerSemaphoreOptions?.filter(
						(o) => o.value === playerData.semaphore,
					)?.[0]?.label
				: null,
			label: t("zone"),
			size: "col-6 col-lg-4",
		});
	}

	const appearanceConfiguration = [
		{
			value: playerData?.lastWeight
				? Number(playerData?.lastWeight?.toFixed(1)) + " kg"
				: "---",
			label: "Peso",
			size: "col-6 col-lg-4",
		},
		{
			value: Number(playerData?.weightHeightRatio?.toFixed(1)),
			label: "Peso/altezza",
			size: "col-6 col-lg-4",
		},
		{
			value: Number(playerData?.bmi?.toFixed(1)),
			label: "BMI",
			size: "col-6 col-lg-4",
		},

		{
			value: playerData?.lastHeight
				? Number(playerData?.lastHeight?.toFixed(1)) + " cm"
				: "---",
			label: "Altezza",
			size: "col-6 col-lg-4",
		},

		{
			value: Number(playerData?.heightWeightRatio?.toFixed(1)),
			label: "Altezza/peso",
			size: "col-6 col-lg-4",
		},
	];

	const statusLabel = useMemo(() => {
		if (!playerData?.status) {
			return null;
		}

		return (
			statusOptions?.find((so) => so.value === playerData?.status)?.label ||
			playerData?.status
		);
	}, [playerData]);

	const zoneLabel = useMemo(() => {
		if (!playerData?.semaphore) {
			return null;
		}

		return (
			playerSemaphoreOptions?.find((o) => o.value === playerData?.semaphore)
				?.label || playerData?.semaphore
		);
	}, [playerData]);

	const heroChips = useMemo(() => {
		if (!playerData || !Object.keys(playerData).length) {
			return [];
		}

		const chips = [];

		if (playerData?.primaryRole) {
			chips.push({id: "role", label: playerData.primaryRole, tone: "primary"});
		}

		if (playerData?.playerCategoryName) {
			chips.push({
				id: "category",
				label: playerData.playerCategoryName,
				tone: "neutral",
			});
		}

		if (statusLabel) {
			chips.push({
				id: "status",
				label: statusLabel,
				tone: playerData?.status === "Interno" ? "success" : "warning",
			});
		}

		// if (playerData?.goalkeeper) {
		// 	const gkLabel = t("goalkeeper");
		// 	chips.push({
		// 		id: "goalkeeper",
		// 		label: gkLabel === "goalkeeper" ? "Portiere" : gkLabel,
		// 		tone: "accent",
		// 	});
		// }

		if (playerData?.prospect) {
			chips.push({
				id: "prospect",
				label: "Prospect",
				tone: "info",
			});
		}

		return chips;
	}, [playerData, statusLabel, t]);

	const playerRating = useMemo(() => {
		if (!valuationsData || !valuationsData.length) {
			return null;
		}

		let sum = 0;
		let count = 0;

		valuationsData.forEach((valuation) => {
			if (
				valuation.finalAverage !== null &&
				valuation.finalAverage !== undefined
			) {
				const value =
					typeof valuation.finalAverage === "number"
						? valuation.finalAverage
						: parseFloat(valuation.finalAverage);

				if (!Number.isNaN(value)) {
					sum += value;
					count += 1;
				}
			}
		});

		if (!count) {
			return null;
		}

		return sum / count;
	}, [valuationsData]);

	const matchReportsStats = useMemo(() => {
		if (!matchReports || !matchReports.length) {
			return {average: null, count: 0, trend: []};
		}

		let sum = 0;
		let count = 0;
		const trend = matchReports
			.map((report) => {
				const value =
					report?.generalRating !== null && report?.generalRating !== undefined
						? parseFloat(report.generalRating)
						: null;
				if (value !== null && !Number.isNaN(value)) {
					sum += value;
					count += 1;
				}
				return {
					date: report?.reportDate,
					rating: value !== null && !Number.isNaN(value) ? value : null,
				};
			})
			.filter((item) => item.rating !== null && item.date)
			.sort((a, b) => new Date(a.date) - new Date(b.date));

		return {
			average: count ? sum / count : null,
			count,
			trend,
		};
	}, [matchReports]);

	const heroHighlights = useMemo(() => {
		if (!playerData || !Object.keys(playerData).length) {
			return [];
		}

		const highlights = [];

		if (playerRating !== null) {
			highlights.push({
				id: "rating",
				label: t("player_rating", {defaultValue: "Rating giocatore"}),
				value: Number(playerRating.toFixed(2)),
				isEmphasized: true,
			});
		}

		if (matchReportsStats.average !== null) {
			highlights.push({
				id: "match_rating",
				label: "Rating gara",
				value: Number(matchReportsStats.average.toFixed(2)),
				isEmphasized: true,
			});
		}

		highlights.push(
			{
				id: "birthdate",
				label: t("birthdate"),
				value: playerData?.dateOfBirth
					? moment(playerData?.dateOfBirth).format(dateFormat)
					: null,
			},
			{
				id: "birthplace",
				label: t("place_of_birth", {defaultValue: "Luogo di nascita"}),
				value: playerData?.placeOfBirth,
			},
			{
				id: "nationality",
				label: t("nationality"),
				value: playerData?.nationality,
			},
			{
				id: "address",
				label: t("address"),
				value: playerData?.address,
			},
			{
				id: "phone",
				label: t("phone"),
				value: playerData?.telephone,
			},
			{
				id: "email",
				label: "Email",
				value: playerData?.email,
			},
		);

		if (statusLabel) {
			highlights.push({
				id: "status-highlight",
				label: "Status",
				value: statusLabel,
			});
		}

		if (zoneLabel && user?.roleName === "Admin") {
			highlights.push({
				id: "zone-highlight",
				label: t("zone"),
				value: zoneLabel,
			});
		}

		return highlights.filter((item) => item.value);
	}, [
		playerData,
		playerRating,
		matchReportsStats.average,
		statusLabel,
		zoneLabel,
		t,
		user?.roleName,
	]);

	const physicalHeightColumns = [
		{
			label: t("date_label"),
			key: "date",
			customRender: (item) => {
				return moment(item.date).format(dateFormat);
			},
		},
		{
			label: "Altezza",
			key: "height",
			customRender: (item) => {
				return item.height + " cm";
			},
		},
	];
	const physicalWeightColumns = [
		{
			label: t("date_label"),
			key: "date",
			customRender: (item) => {
				return moment(item.date).format(dateFormat);
			},
		},
		{
			label: "Peso",
			key: "weight",
			customRender: (item) => {
				return item.weight + " kg";
			},
		},
	];
	const phvColumns = [
		{
			label: t("date_label"),
			key: "date",
			customRender: (item) => {
				return moment(item.date).format(dateFormat);
			},
		},
		{
			label: "Altezza",
			key: "height",
			customRender: (item) => {
				return item.height ? item.height + " cm" : "---";
			},
		},
		{
			label: "Peso",
			key: "weight",
			customRender: (item) => {
				return item.weight ? item.weight + " kg" : "---";
			},
		},
		{
			label: "Altezza madre",
			key: "motherHeight",
			customRender: (item) => {
				return playerData.motherHeight
					? playerData.motherHeight + " cm"
					: "---";
			},
		},
		{
			label: "Altezza padre",
			key: "fatherHeight",
			customRender: (item) => {
				return playerData.fatherHeight
					? playerData.fatherHeight + " cm"
					: "---";
			},
		},
		{
			label: "PAH",
			key: "pah",
			customRender: (item) => {
				return (
					<div style={{color: "green"}}>{item.pah ? item.pah : "---"}</div>
				);
			},
		},
		{
			label: "% PAH",
			key: "pahPercent",
			customRender: (item) => {
				return (
					<div style={{color: "green"}}>
						{item.pahPercent ? item.pahPercent : "---"}
					</div>
				);
			},
		},
		{
			label: "Mirwald APHV",
			key: "mirwald",
			customRender: (item) => {
				return (
					<div style={{color: "green"}}>
						{item.mirwald ? item.mirwald : "---"}
					</div>
				);
			},
		},
		{
			label: "Moore APHV",
			key: "moore",
			customRender: (item) => {
				return (
					<div style={{color: "green"}}>{item.moore ? item.moore : "---"}</div>
				);
			},
		},
	];
	const measurementsColumns = [
		{
			label: t("date_label"),
			key: "date",
			customRender: (item) => {
				return moment(item.date).format(dateFormat);
			},
		},
		{
			label: "IMG",
			key: "fatMassIndex",
			customRender: (item) => {
				return item.fatMassIndex ? item.fatMassIndex : "---";
			},
		},
		{
			label: "IMM",
			key: "leanMassIndex",
			customRender: (item) => {
				return item.leanMassIndex ? item.leanMassIndex : "---";
			},
		},
		{
			label: "LS",
			key: "shoulderWidth",
			customRender: (item) => {
				return item.shoulderWidth ? item.shoulderWidth + " cm" : "---";
			},
		},
		{
			label: "CT",
			key: "chestCircumference",
			customRender: (item) => {
				return item.chestCircumference
					? item.chestCircumference + " cm"
					: "---";
			},
		},
		{
			label: "CA",
			key: "abdomenCircumference",
			customRender: (item) => {
				return item.abdomenCircumference
					? item.abdomenCircumference + " cm"
					: "---";
			},
		},
		{
			label: "CF",
			key: "hipCircumference",
			customRender: (item) => {
				return item.hipCircumference ? item.hipCircumference + " cm" : "---";
			},
		},
		{
			label: "CB dx",
			key: "rightArmCircumference",
			customRender: (item) => {
				return item.rightArmCircumference
					? item.rightArmCircumference + " cm"
					: "---";
			},
		},
		{
			label: "CB sx",
			key: "leftArmCircumference",
			customRender: (item) => {
				return item.leftArmCircumference
					? item.leftArmCircumference + " cm"
					: "---";
			},
		},
		{
			label: "CG dx",
			key: "rightLegCircumference",
			customRender: (item) => {
				return item.rightLegCircumference
					? item.rightLegCircumference + " cm"
					: "---";
			},
		},
		{
			label: "CG sx",
			key: "leftLegCircumference",
			customRender: (item) => {
				return item.leftLegCircumference
					? item.leftLegCircumference + " cm"
					: "---";
			},
		},
	];
	const contactsColumns = [
		{label: t("name"), key: "name", align: "start", medium: true},
		{label: "Email", key: "email", align: "start"},
		{label: "Telefono", key: "telephone", align: "start", small: true},
		{label: "Parentela", key: "relation", align: "start", small: true},
	];
	const agentsColumns = [
		{
			label: t("from"),
			key: "dateFrom",
			align: "center",
			medium: true,
			customRender: (item) => moment(item.dateFrom).format(dateFormat),
		},
		{label: t("name"), key: "name", align: "start"},
		{label: "Note", key: "notes", align: "start"},
	];
	const careerColumns = [
		{
			label: t("season"),
			key: "season",
			medium: true,
			customRender: (item) => item.season?.name,
		},
		{
			label: t("team"),
			key: "club",
			align: "start",
			customRender: (item) => item.club?.name,
		},
		{
			label: t("category"),
			key: "playerCategory",
			align: "start",
			customRender: (item) => item.playerCategory?.name,
		},
	];
	const movementsColumns = [
		{
			label: t("season"),
			key: "movementDate",
			align: "center",
			small: true,
			customRender: (item) => {
				const currentYear = moment(item.movementDate).year();
				const currentMonth = moment(item.movementDate).month() + 1;

				return currentMonth <= 6
					? "" + (currentYear - 1).toString() + "/" + currentYear
					: "" + currentYear + "/" + (currentYear + 1);
			},
		},
		{
			label: "Società",
			key: "club?.name",
			align: "start",
			medium: true,
			customRender: (item) => item.club?.name,
		},
		{
			label: "Tesseramento",
			key: "membershipDate",
			align: "center",
			medium: true,
			customRender: (item) => moment(item.membershipDate).format(dateFormat),
		},
		{
			label: "Status",
			key: "movementType?.status",
			align: "start",
			medium: true,
			customRender: (item) => item.movementType?.status,
		},
		{
			label: t("date_label"),
			key: "movementDate",
			align: "center",
			medium: true,
			customRender: (item) => moment(item.movementDate).format(dateFormat),
		},
		{
			label: "Tipo",
			key: "movementType",
			align: "start",
			customRender: (item) => item.movementType?.description,
		},
	];

	const handleClose = () => {
		setOpenPhysicInfoWeight(false);
		setOpenPhysicHeightInfo(false);
		setOpenPHVInfo(false);
		setOpenMeasurementInfo(false);
		setOpenContact(false);
		setOpenAgents(false);
		setOpenMovement(false);
		setInitialData();
	};

	const handlePhysicInfo = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				playerId: playerData?.id,
				...data,
				height: !data.height ? "0" : data.height,
				date: moment(data.date).format("YYYY-MM-DD"),
			};
			id
				? await PlayerAPI.updateGrowth(playerData?.id, id, body)
				: await PlayerAPI.createGrowth(body);
			handleClose();
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const deletePhysicInfo = async (id) => {
		setLoading(true);
		try {
			await PlayerAPI.deleteGrowth(playerData?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleContact = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				playerId: playerData?.id,
				contactRole: data?.contactRole || "",
				...data,
			};
			id
				? await PlayerAPI.updateContact(playerData?.id, id, body)
				: await PlayerAPI.createContact(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
			handleClose();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const deleteContact = async (id) => {
		setLoading(true);
		try {
			await PlayerAPI.deleteContact(playerData?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleAgent = async (data, id) => {
		setLoading(true);
		try {
			let body = {
				playerId: playerData?.id,
				...data,
				dateFrom: moment(data?.dateFrom).format("YYYY-MM-DD"),
			};
			if (!id) {
				body = [body];
			}
			id
				? await PlayerAPI.updateAgent(playerData?.id, id, body)
				: await PlayerAPI.createAgent(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
			handleClose();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const deleteAgent = async (id) => {
		setLoading(true);
		try {
			await PlayerAPI.deleteAgent(playerData?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleCareerDetail = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				...data,
				playerId: playerData?.id,
				seasonId: data?.careerSeason?.value,
				clubId: data?.careerClub?.value,
				playerCategoryId: data?.careerCategory?.value,
			};

			id
				? await PlayerAPI.updateCareer(playerData?.id, id, body)
				: await PlayerAPI.createCareer(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
			setOpenCareerDetail(false);
			setInitialData();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteCareerDetail = async (id) => {
		setLoading(true);
		try {
			await PlayerAPI.deleteCareer(playerData?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleMovement = async (data, id) => {
		setLoading(true);

		try {
			let body = {
				id,
				movementDate: moment(data?.movementDate).format("YYYY-MM-DD"),
				playerId: playerData?.id,
				typeCode: data?.movement?.code,
				player: null,
				clubId: null,
				club: null,
			};

			if (data?.movement?.updateStatus) {
				body = {...body, player: {...playerData, status: data?.status}};
			}

			if (data?.movement?.updateMembershipDate) {
				body = {
					...body,
					membershipDate: moment(data?.membershipDate).format("YYYY-MM-DD"),
				};
			}

			if (data?.movement?.defineAnotherClub) {
				body.clubId = data?.club?.id;
				body.club = data?.club;

				body = {
					...body,
					clubId: data?.club?.id,
					club: data?.club,
				};
			}

			id
				? await PlayerAPI.updatePlayerMovement(id, body)
				: await PlayerAPI.createPlayerMovement(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayerMovements();
			handleClose();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteMovement = async (id) => {
		setLoading(true);
		try {
			await PlayerAPI.deletePlayerMovement(id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayerMovements();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	// const handleNote = async (data, id) => {
	// 	setLoading(true);
	// 	try {
	// 		const body = {
	// 			...data,
	// 			date: moment().format("YYYY-MM-DD"),
	// 			playerId: playerData?.id,
	// 		};
	// 		id
	// 			? await PlayerAPI.updateNote(playerData?.id, id, body)
	// 			: await PlayerAPI.createNote(body);
	// 		handleNotification(SUCCESSFUL_OPERATION, "success");
	// 		getPlayer();
	// 		handleClose();
	// 	} catch (error) {
	// 		handleNotification(ERROR_OPERATION, "error");
	// 		setLoading(false);
	// 	}
	// };

	// const deleteNote = async (id) => {
	// 	setLoading(true);
	// 	try {
	// 		await PlayerAPI.deleteNote(playerData?.id, id);
	// 		handleNotification(SUCCESSFUL_OPERATION, "success");
	// 		getPlayer();
	// 	} catch (error) {
	// 		handleNotification(ERROR_OPERATION, "error");
	// 		setLoading(false);
	// 	}
	// };

	const breadcrumbsElements = [
		{
			label: "Giocatori",
			path: "/players",
		},
		{label: "/"},
	];

	const rpeValues = {};
	playerData?.playerRpe?.forEach((pr) => {
		if (!rpeValues[pr.rpe]) rpeValues[pr.rpe] = 0;
		rpeValues[pr.rpe] += 1;
	});

	const rpeChartData = [];
	Object.keys(rpeValues)?.forEach((rv) => {
		rpeChartData.push({
			name: rv,
			value: rpeValues[rv],
		});
	});

	const currentTab = useSelector((state) => state.playerTab);

	let mainActions = [];
	switch (currentTab) {
		case "main_info":
			mainActions =
				user?.userName === "CaputoD"
					? [
							{
								icon: <ArrowBackIcon />,
								title: "Indietro",
								onClick: () => navigate("/players"),
								color: "info",
							},
						]
					: [
							{
								icon: <ArrowBackIcon />,
								title: "Indietro",
								onClick: () => navigate("/players"),
								color: "info",
							},
							{
								icon: <EditIcon />,
								title: "Modifica",
								onClick: () => navigate("/players/" + playerData?.id + "/edit"),
							},
							{
								icon: <DeleteIcon />,
								title: "Elimina",
								onClick: () => setOpenHandleConfirm(true),
								color: "error",
							},
						];
			break;
		case "stats":
		case "performance":
		case "gps":
		case "medical":
			mainActions = [
				{
					icon: <ArrowBackIcon />,
					title: "Indietro",
					onClick: () => navigate("/players"),
					color: "info",
				},
			];
			break;
		case "athletic_data":
			mainActions = [
				{
					icon: <ArrowBackIcon />,
					title: "Indietro",
					onClick: () => navigate("/players"),
					color: "info",
				},
			];
			break;
		case "valuations":
			mainActions =
				playerData?.status !== "Esterno"
					? [
							{
								icon: <ArrowBackIcon />,
								title: "Indietro",
								onClick: () => navigate("/players"),
								color: "info",
							},
							{
								icon: <AddIcon />,
								title: "Aggiungi",
								onClick: () =>
									navigate("/players-valuations/new?playerId=" + id),
							},
						]
					: [
							{
								icon: <ArrowBackIcon />,
								title: "Indietro",
								onClick: () => navigate("/players"),
								color: "info",
							},
						];
			break;
		default:
			break;
	}

	const getChartFilters = (filter, setFilter, date, setDate) => {
		return (
			<div className="col-sm-12">
				<div className="stat-options">
					<div className="stat-switch">
						<div
							className={
								"single-option clickable" +
								(filter === "seasonal" ? " selected" : "")
							}
							onClick={() => {
								setFilter("seasonal");
							}}
						>
							Stagionale
						</div>
						<div
							className={
								"single-option clickable" +
								(filter === "monthly" ? " selected" : "")
							}
							onClick={() => {
								setFilter("monthly");
							}}
						>
							Mensile
						</div>
					</div>
					{filter === "monthly" ? (
						<div className="stat-input mx-5">
							<VLFDatePicker
								type="month"
								customDateFormat="MM/yyyy"
								value={date}
								onChange={setDate}
								required
								size="small"
							/>
						</div>
					) : null}
				</div>
			</div>
		);
	};
	const getChartStats = (stats, fieldKey, filter) => {
		return (
			<div className="col-sm-12">
				<div className="stats-box-container mt-2">
					{stats.map((s, k) => {
						return (
							<div key={k} className="stat-box">
								<div className="stat-value">
									{Math.round(s[fieldKey] * 100) / 100}
								</div>
								<div className="stat-label">
									{filter === "monthly"
										? "Settimana " + (s.weekNumber || "")
										: s.year
											? moment()
													.set({
														month: s.month - 1,
														year: s.year,
													})
													.format("MMMM YYYY")
											: ""}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	const [tqrStats, setTQRStats] = useState([]);
	const [rpeStats, setRPEStats] = useState([]);
	const [matchesStats, setMatchesStats] = useState({});
	const [playedMatches, setPlayedMatches] = useState([]);
	const [trainingsStats, setTrainingsStats] = useState([]);
	const [totalMatchesStats, setTotalMatchesStats] = useState({});

	const [tqrFilter, setTQRFilter] = useState("seasonal");
	const [tqrDate, setTQRDate] = useState(new Date());
	const [rpeFilter, setRPEFilter] = useState("seasonal");
	const [rpeDate, setRPEDate] = useState(new Date());
	const [matchesFilter, setMatchesFilter] = useState("seasonal");
	const [matchesDate, setMatchesDate] = useState(new Date());
	const [trainingsFilter, setTrainingsFilter] = useState("weekly");
	const [trainingsDate, setTrainingsDate] = useState(new Date());
	const [quickStatsView, setQuickStatsView] = useState("total");

	const getRPEStats = async () => {
		try {
			const response =
				rpeFilter === "monthly"
					? await StatAPI.weeklyRPE({
							groupMode: 2,
							playerId: id,
							month: moment(rpeDate).month() + 1,
							year: moment(rpeDate).year(),
						})
					: await StatAPI.monthlyRPE({groupMode: 1, playerId: id, seasonId});
			setRPEStats(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};
	const getTQRStats = async () => {
		try {
			const response =
				tqrFilter === "monthly"
					? await StatAPI.weeklyTQR({
							groupMode: 2,
							playerId: id,
							month: moment(tqrDate).month() + 1,
							year: moment(tqrDate).year(),
						})
					: await StatAPI.monthlyTQR({groupMode: 1, playerId: id, seasonId});
			setTQRStats(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};
	const getMatchesStats = async () => {
		try {
			const response =
				matchesFilter === "monthly"
					? await StatAPI.monthlyMatches(id, {
							grouped: true,
							month: moment(matchesDate).month() + 1,
							year: moment(matchesDate).year(),
						})
					: await StatAPI.monthlyMatches(id, {grouped: true, seasonId});

			const listResponse =
				matchesFilter === "monthly"
					? await StatAPI.matchesDetails(id, {
							month: moment(matchesDate).month() + 1,
							year: moment(matchesDate).year(),
						})
					: await StatAPI.matchesDetails(id, {seasonId});
			setPlayedMatches(listResponse);

			let stats = {};
			response.forEach((el) => {
				if (!stats[el.year + "-" + el.month])
					stats[el.year + "-" + el.month] = {};
				if (!stats[el.year + "-" + el.month][el.matchType])
					stats[el.year + "-" + el.month][el.matchType] = {};

				if (playerData?.goalkeeper) {
					stats[el.year + "-" + el.month][el.matchType].goalsConceded =
						el.goalsConceded + "/" + el.totalGoalsConceded;
				}
				stats[el.year + "-" + el.month][el.matchType].playedMinutes =
					el.totalMinutes + "/" + el.matchTotalMinutes;
				stats[el.year + "-" + el.month][el.matchType].matches =
					el.convocations + "/" + el.totalMatches;
			});

			const totalStats = {};
			matchTypes.forEach((mt) => {
				Object.keys(stats).forEach((sm) => {
					const current = stats[sm][mt];
					if (current) {
						const currentGoalsConceded = playerData?.goalkeeper
							? current.goalsConceded.split("/")
							: undefined;
						const currentPlayedMinutes = current.playedMinutes.split("/");
						const currentMatches = current.matches.split("/");

						if (!totalStats[mt]) totalStats[mt] = {};
						if (playerData?.goalkeeper) {
							totalStats[mt].goalsConceded =
								parseInt(totalStats[mt].goalsConceded || 0) +
								parseInt(currentGoalsConceded[0]);
							totalStats[mt].totalGoalsConceded =
								parseInt(totalStats[mt].totalGoalsConceded || 0) +
								parseInt(currentGoalsConceded[1]);
						}

						totalStats[mt].playedMinutes =
							parseInt(totalStats[mt].playedMinutes || 0) +
							parseInt(currentPlayedMinutes[0]);
						totalStats[mt].totalPlayedMinutes =
							parseInt(totalStats[mt].totalPlayedMinutes || 0) +
							parseInt(currentPlayedMinutes[1]);

						totalStats[mt].convocations =
							parseInt(totalStats[mt].convocations || 0) +
							parseInt(currentMatches[0]);
						totalStats[mt].matches =
							parseInt(totalStats[mt].matches || 0) +
							parseInt(currentMatches[1]);
					}
				});
			});

			setTotalMatchesStats(totalStats);

			Object.keys(stats).forEach((mk) => {
				stats[mk] = Object.keys(stats[mk])
					.sort()
					.reduce((obj, key) => {
						obj[key] = stats[mk][key];
						return obj;
					}, {});
			});

			setMatchesStats(stats);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};
	const getTrainingsStats = async () => {
		try {
			const currentWeek = moment(trainingsDate).week();
			const response =
				trainingsFilter === "daily"
					? await StatAPI.trainingsDetails(id, {
							playerId: id,
							yearWeek:
								moment(trainingsDate).year() +
								"" +
								(currentWeek < 10 ? "0" + currentWeek : currentWeek),
						})
					: await StatAPI.weeklyTrainings(id, {
							playerId: id,
							month: moment(trainingsDate).month() + 1,
							year: moment(trainingsDate).year(),
							grouped: true,
						});
			setTrainingsStats(
				response
					.filter((r) => moment(r.trainingDate).year() < 2050)
					.map((r) => {
						if (r.trainingData) {
							r.startDate = r.trainingData.startDate;
						}
						return r;
					}),
			);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getPlaylists = async () => {
		setLoading(true);
		try {
			const response = await PlaylistAPI.list({playerId: id});
			const videos = [];
			response?.forEach((p) => videos.push(...p.videos));
			setPlaylists(videos);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (categories?.length) {
			if (currentTab === "main_info") {
				getPlayerMovements();
				getPlayerAttachments();
			}

			if (currentTab === "valuations") {
				getPlayerNotes();
			}

			if (currentTab === "performance") {
				getPlayerGPS();
			}
		}
	}, [currentTab, categories]);

	useEffect(() => {
		if (currentTab === "main_info" || currentTab === "valuations") {
			getValuations();
			getMatchReports();
		}
	}, [currentTab, playerData]);

	useEffect(() => {
		if (currentTab === "main_info" || currentTab === "stats") {
			getQuickStats();
		}
	}, [currentTab, playerData]);

	useEffect(() => {
		if (currentTab === "stats") {
			getMatchesStats();
		}
	}, [currentTab, matchesFilter, matchesDate]);

	useEffect(() => {
		if (currentTab === "stats") {
			getTrainingsStats();
		}
	}, [currentTab, trainingsFilter, trainingsDate]);

	useEffect(() => {
		if (currentTab === "video") {
			getPlaylists();
		}
	}, [currentTab]);

	// ---

	useEffect(() => {
		if (currentTab === "performance") {
			getRPEStats();
		}
	}, [currentTab, rpeFilter, rpeDate]);
	useEffect(() => {
		if (currentTab === "performance") {
			getTQRStats();
		}
	}, [currentTab, tqrFilter, tqrDate]);

	useEffect(() => {
		if (
			currentTab &&
			tabs?.filter((t) => t.key === currentTab)?.[0].hidden &&
			Object.keys(playerData).length > 0
		) {
			dispatch(setPlayerTab("main_info"));
		}
	}, [currentTab, playerData]);

	const groupedTests = {};
	[{name: "Corse"}, {name: "Spinte"}].forEach((c) => {
		const currentType = c.name;
		const testsItems = playerData?.athleticTests
			?.filter((a) => a.testType === currentType)
			.map((a) => {
				a.playerId = id;
				return a;
			});

		groupedTests[currentType] = testsItems;
	});

	const listConfiguration = {
		data: groupedTests,
		onClick: (test) => navigate("/athletic-tests/" + test.testId),
	};

	const columns = [
		{
			label: t("date_label"),
			key: "testDate",
			customRender: (item) => {
				return moment(item.testDate).format(dateFormat);
			},
			small: true,
		},
	];

	const runningColumns = [...columns];
	const jumpingColumns = [...columns];
	const resistenceColumns = [...columns];
	const agilityColumns = [...columns];
	const strengthColumns = [...columns];

	const runningData = playerData?.athleticTests?.filter(
		(t) => t.testType === "Corse",
	);
	const jumpingData = playerData?.athleticTests?.filter(
		(t) => t.testType === "Spinte",
	);
	const resistenceData = playerData?.athleticTests?.filter(
		(t) => t.testType === "Test di resistenza",
	);
	const agilityData = playerData?.athleticTests?.filter(
		(t) => t.testType === "Test di agilità",
	);
	const strengthData = playerData?.athleticTests?.filter(
		(t) => t.testType === "Test di forza esplosiva",
	);

	const runningParameters = testTypes?.find((t) => t.value === "Corse")?.params;
	runningParameters?.forEach((td) => {
		runningColumns.push({
			label: td.value,
			key: "value",
			customRender: (item) => {
				const currentValue = item.details?.filter(
					(d) => d.valueType === td.value,
				)?.[0];
				return currentValue?.value || "---";
			},
			align: "center",
			small: true,
		});
	});

	const jumpingParameters = testTypes?.find(
		(t) => t.value === "Spinte",
	)?.params;
	jumpingParameters?.forEach((td) => {
		jumpingColumns.push({
			label: td.value,
			key: "value",
			customRender: (item) => {
				const currentValue = item.details?.filter(
					(d) => d.valueType === td.value,
				)?.[0];
				return currentValue?.value || "---";
			},
			align: "center",
			small: true,
		});
	});

	resistenceData?.[resistenceData.length - 1]?.details?.forEach((td) => {
		resistenceColumns.push({
			label: td.valueType,
			key: "value",
			customRender: (item) => {
				const currentValue = item.details?.filter(
					(d) => d.valueType === td.valueType,
				)?.[0];
				return currentValue?.value || "---";
			},
			align: "end",
			small: true,
		});
	});
	agilityData?.[agilityData.length - 1]?.details?.forEach((td) => {
		agilityColumns.push({
			label: td.valueType,
			key: "value",
			customRender: (item) => {
				const currentValue = item.details?.filter(
					(d) => d.valueType === td.valueType,
				)?.[0];
				return currentValue?.value || "---";
			},
			align: "end",
			small: true,
		});
	});
	strengthData?.[strengthData.length - 1]?.details?.forEach((td) => {
		strengthColumns.push({
			label: td.valueType,
			key: "value",
			customRender: (item) => {
				const currentValue = item.details?.filter(
					(d) => d.valueType === td.valueType,
				)?.[0];
				return currentValue?.value || "---";
			},
			align: "end",
			small: true,
		});
	});

	const playedMatchesConfiguration = {
		data: playedMatches,
		onClick: (match) => navigate("/matches/" + match.matchId),
	};

	const playedMatchesColumns = [
		{
			label: t("date_label"),
			key: "matchDate",
			customRender: (item) => {
				return moment(item.matchDate).format(dateFormat);
			},
			small: true,
		},
		{
			label: t("category"),
			key: "playersCategory",
			small: true,
			align: "start",
			customRender: (item) => {
				return <div>{item.playersCategory}</div>;
			},
		},
		{
			label: "Partita",
			key: "match",
			align: "start",
			customRender: (item) => {
				return item.homeClub + " - " + item.awayClub;
			},
		},
		{
			label: "Risultato",
			key: "result",
			small: true,
			align: "center",
			customRender: (item) => {
				return item.composedResult;
			},
		},
		{
			label: "Tipologia",
			key: "matchType",
			medium: true,
			align: "start",
		},
		{
			label: "Presenza gara",
			key: "convocationStatus",
			customRender: (item) => {
				switch (item.convocationStatus) {
					case "Presente":
						return <div className="cell on" />;
					case "Assente":
					case "Non convocato":
						return <div className="cell off" />;
					case "Infortunato":
						return <LocalHospitalIcon color="error" />;
					default:
						return "";
				}
			},
			small: true,
		},
		{
			label: "Minuti giocati",
			key: "minutesPlayed",
			small: true,
			align: "end",
			customRender: (item) => {
				return item.minutesPlayed !== null && item.minutesPlayed !== undefined
					? item.minutesPlayed + "/" + item.matchMinutes
					: "---";
			},
		},
	];
	if (playerData?.goalkeeper) {
		playedMatchesColumns.push({
			label: "Goal subiti",
			key: "goalsConceded",
			small: true,
			align: "end",
			customRender: (item) => {
				return item.goalsConceded !== null && item.goalsConceded !== undefined
					? item.goalsConceded + "/" + item.totalGoalsConceded
					: "---";
			},
		});
	}

	const trainingsDailyColumns = [
		{
			label: t("date_label"),
			key: "trainingDate",
			customRender: (item) => {
				return moment(item.trainingDate).format(dateFormat);
			},
			small: true,
		},
		{
			label: t("time_label"),
			key: "startDate",
			customRender: (item) => {
				return moment(item.trainingData?.startDate).format("HH:mm");
			},
			small: true,
		},
		{
			label: t("category"),
			key: "trainingData.playersCategory",
			small: true,
			align: "start",
		},
		{
			label: "Durata (min)",
			key: "trainingsDuration",
			small: true,
			align: "end",
		},
		{
			label: "RPE",
			key: "playerRpe",
			small: true,
			align: "end",
		},
		{
			label: "Training load",
			key: "trainingLoad",
			small: true,
			align: "end",
		},
	];
	const trainingsWeeklyColumns = [
		{
			label: t("from"),
			key: "weekStartDate",
			customRender: (item) => {
				return moment(item.weekStartDate).format(dateFormat);
			},
			small: true,
		},
		{
			label: t("to"),
			key: "weekEndDate",
			customRender: (item) => {
				return moment(item.weekEndDate).format(dateFormat);
			},
			small: true,
		},
		{
			label: "Allenamenti",
			key: "totalTrainings",
			small: true,
			align: "end",
		},
		{
			label: "In altra categoria",
			key: "otherConvocations",
			small: true,
			align: "end",
		},
		{
			label: "In 1° squadra",
			key: "firstTeamConvocations",
			small: true,
			align: "end",
		},
		{
			label: "RPE minimo",
			key: "minRpe",
			small: true,
			align: "end",
		},
		{
			label: "RPE massimo",
			key: "maxRpe",
			small: true,
			align: "end",
		},
		{
			label: "RPE medio",
			key: "avgRpe",
			small: true,
			align: "end",
		},
		{
			label: "Training load",
			key: "trainingLoad",
			small: true,
			align: "end",
		},
	];

	const trainingsValues =
		trainingsFilter === "daily"
			? [
					{value: "trainingsDuration", label: "Durata (min.)"},
					{value: "playerRpe", label: "RPE"},
					{value: "trainingLoad", label: "Training load"},
				]
			: [
					{value: "totalTrainings", label: "Allenamenti"},
					{value: "otherConvocations", label: "In altra categoria"},
					{value: "firstTeamConvocations", label: "In 1° squadra"},
					{value: "minRpe", label: "RPE minimo"},
					{value: "maxRpe", label: "RPE massimo"},
					{value: "avgRpe", label: "RPE medio"},
					{value: "trainingLoad", label: "Training load"},
				];

	const careerConfiguration = {
		data: playerMovements,
		actions: [
			{
				icon: <EditIcon />,
				variant: "outlined",
				title: "Modifica",
				onClick: (item) => {
					setInitialData(Object.assign({}, item));
					setOpenMovement(true);
				},
			},
			{
				icon: <DeleteIcon />,
				variant: "outlined",
				title: "Elimina",
				onClick: (item) => {
					deleteMovement(item.id);
				},
				color: "error",
			},
		],
	};

	const normalizeStatValue = (value, minutesValue) => {
		const minutes =
			typeof minutesValue === "number"
				? minutesValue
				: parseFloat(minutesValue);
		if (!minutes || Number.isNaN(minutes)) {
			return value ?? 0;
		}

		const numericValue =
			typeof value === "number" ? value : parseFloat(value ?? 0);
		if (Number.isNaN(numericValue)) {
			return value ?? 0;
		}

		return Math.round((numericValue / minutes) * 90 * 100) / 100;
	};

	const getStatsGroups = (data, mode = "total") => {
		const minutesValue =
			data?.minutes ??
			data?.playedMinutes ??
			data?.totalMinutes ??
			data?.matchMinutes;
		const usePer90 = mode === "per90";
		const getValue = (value, options = {}) => {
			if (!usePer90 || options.skipNormalize) {
				return value ?? 0;
			}
			return normalizeStatValue(value, minutesValue);
		};

		if (!playerData?.goalkeeper) {
			return [
				{
					title: "Stats principali",
					items: [
						{
							value: getValue(data?.goals || 0),
							label: "Goal fatti",
						},
						{
							value: getValue(data?.assists || 0),
							label: "Assist",
						},
						{
							value: getValue(data?.keyPasses || 0),
							label: "Passaggi chiave",
						},
					],
				},
			];
		}

		return [
			{
				title: "Generali",
				items: [
					{
						value: `${minutesValue || 0}'`,
						label: "Minutaggio",
						skipNormalize: true,
					},
				],
			},
			{
				title: "Tiri in porta",
				items: [
					{
						value: getValue(data?.saves || 0),
						label: "Parate",
					},
					{
						value: getValue(data?.goalsConceded || 0),
						label: "Goal subiti",
					},
				],
			},
			{
				title: "Letture",
				items: [
					{
						value: getValue(data?.outOfAreaReadings || 0),
						label: "Fuori area",
					},
					{
						value: getValue(data?.lowReadings || 0),
						label: "Area basse",
					},
					{
						value: getValue(data?.highReadings || 0),
						label: "Area alte",
					},
				],
			},
			{
				title: "Passaggi corti",
				items: [
					{
						value: getValue(data?.shortHorizontalPassRight || 0),
						label: "Orizz Dx",
					},
					{
						value: getValue(data?.shortHorizontalPassLeft || 0),
						label: "Orizz Sx",
					},
					{
						value: getValue(data?.shortVerticalPass || 0),
						label: "Verticale",
					},
				],
			},
			{
				title: "Killer pass",
				items: [
					{
						value: getValue(data?.killerPass || 0),
						label: "Killer pass",
					},
				],
			},
			{
				title: "Passaggi lunghi",
				items: [
					{
						value: getValue(data?.longFrontalPass || 0),
						label: "Frontale",
					},
					{
						value: getValue(data?.longPassRight || 0),
						label: "Dx",
					},
					{
						value: getValue(data?.longPassLeft || 0),
						label: "Sx",
					},
				],
			},
		];
	};

	const getStatsRow = (stats, mode = "total") => {
		return getStatsGroups(stats, mode).map((sg, k) => {
			return (
				<div
					key={k}
					className="stats-group"
					style={{
						flex: sg.items.length,
					}}
				>
					<div className="group-title">{sg.title}</div>
					<div className="group-items">
						{sg.items.map((sgi, k2) => {
							const displayValue =
								mode === "per90" && typeof sgi.value === "number"
									? sgi.value.toFixed(2)
									: sgi.value;
							return (
								<div key={k2} className="player-stat">
									<div className="circle">
										<span className="number">{displayValue}</span>
									</div>
									<div className="label">{sgi.label}</div>
								</div>
							);
						})}
					</div>
				</div>
			);
		});
	};

	const tabs = [
		{
			key: "main_info",
			label: t("informations"),
			render: () => {
				const hasValuations = valuationsData?.length;

				return (
					<>
						{/* <div className="row no-margin g-1">
							<div className="col-12 col-lg-6 padding-small">
								<div className="kpi-box-container">
									<div className="kpi-label">Giocatore</div>
									<div className="kpi-value">
										{playerData?.lastName
											? playerData?.firstName + " " + playerData?.lastName
											: "---"}
									</div>
								</div>
							</div>
							<div className="col-12 col-lg-3 padding-small">
								<div className="kpi-box-container">
									<div className="kpi-label">Ruolo</div>
									<div className="kpi-value">
										{playerData?.primaryRole || "---"}
									</div>
								</div>
							</div>
							<div className="col-12 col-lg-3 padding-small">
								<div className="kpi-box-container">
									<div className="kpi-label">Categoria</div>
									<div className="kpi-value">
										{playerData?.playerCategoryName || "---"}
									</div>
								</div>
							</div>
						</div> */}
						<div className="player-page__hero-section">
							<VLFCard
								className="player-spotlight-card"
								openable={false}
								content={
									<div className="player-spotlight">
										<div className="player-spotlight__identity">
											<div className="player-spotlight__eyebrow">
												{t("players_page.overview", {
													defaultValue: "Panoramica giocatore",
												})}
											</div>
											<div className="player-spotlight__name">
												{playerData?.firstName || "---"}{" "}
												{playerData?.lastName || ""}
											</div>
											{heroChips.length ? (
												<div className="player-spotlight__chips">
													{heroChips.map((chip) => (
														<span
															key={chip.id}
															className={`player-chip player-chip--${chip.tone}`}
														>
															{chip.label}
														</span>
													))}
												</div>
											) : null}
										</div>
										<div className="player-spotlight__highlights">
											{heroHighlights.length ? (
												heroHighlights.map((item) => (
													<div
														key={item.id}
														className="player-spotlight__highlight"
													>
														<span className="player-spotlight__highlight-label">
															{item.label}
														</span>
														<span className="player-spotlight__highlight-value">
															{item.value}
														</span>
													</div>
												))
											) : (
												<EmptyData />
											)}
										</div>
									</div>
								}
							/>
						</div>
						<div className="row no-margin mt-2">
							<div className="col-sm-12 padding-small">
								<VLFCard
									title={t("main_information")}
									content={
										<>
											<div className="row">
												<div className="col-12 col-xl-6">
													<PlayerRadarChart
														playerData={playerData}
														valuationsData={valuationsData}
													/>
												</div>
												<div className="col-12 col-xl-6">
													<ViewField
														configuration={[
															...teamConfiguration,
															...appearanceConfiguration,
														]}
													/>
												</div>
											</div>
											{Object.keys(quickStats || {}).length ? (
												<div className="quick-stats-hero">
													<div className="stat-options">
														<div className="stat-switch">
															<div
																className={
																	"single-option clickable" +
																	(quickStatsView === "total"
																		? " selected"
																		: "")
																}
																onClick={() => setQuickStatsView("total")}
															>
																Totali
															</div>
															<div
																className={
																	"single-option clickable" +
																	(quickStatsView === "per90"
																		? " selected"
																		: "")
																}
																onClick={() => setQuickStatsView("per90")}
															>
																Per 90'
															</div>
														</div>
													</div>
													<div className="quick-stats-grid">
														{getStatsRow(quickStats, quickStatsView)}
													</div>
												</div>
											) : null}
										</>
									}
								/>
							</div>
						</div>
						{/* <div className="row no-margin mt-2 col-container">
							<div className={"col-sm-12 col-lg-6 padding-small"}>
								<VLFCard
									title="Altre informazioni"
									content={<ViewField configuration={teamConfiguration} />}
									openable={false}
								/>
							</div>

							{playerData?.prospect === true ? (
								""
							) : (
								<div className="col-sm-12 col-lg-6 padding-small">
									<VLFCard
										title="Aspetto"
										content={
											<ViewField configuration={appearanceConfiguration} />
										}
										openable={false}
									/>
								</div>
							)}
						</div> */}
						<div className="row no-margin mt-2">
							<div className="col-sm-12 padding-small">
								<CardsTable
									title="Contatti"
									fullHeight={false}
									middleHeight
									configurationColumns={contactsColumns}
									data={playerData?.playersContacts}
									actions={
										user?.userName !== "CaputoD"
											? [
													{
														icon: <AddIcon />,
														onClick: () => setOpenContact(true),
													},
												]
											: []
									}
									rowActions={user?.userName !== "CaputoD"}
									handleEdit={(item) => {
										setInitialData(Object.assign({}, item));
										setOpenContact(true);
									}}
									handleDelete={(item) => {
										deleteContact(item.id);
									}}
								/>
							</div>
						</div>
						<div className="row no-margin mt-2">
							<div className="col-sm-12 padding-small">
								<CardsTable
									title="Agenti"
									fullHeight={false}
									middleHeight
									configurationColumns={agentsColumns}
									data={playerData?.playersAgentsHistory}
									actions={
										user?.userName !== "CaputoD"
											? [
													{
														icon: <AddIcon />,
														onClick: () => setOpenAgents(true),
													},
												]
											: []
									}
									rowActions={user?.userName !== "CaputoD"}
									handleEdit={(item) => {
										setInitialData(Object.assign({}, item));
										setOpenAgents(true);
									}}
									handleDelete={(item) => {
										deleteAgent(item.id);
									}}
								/>
							</div>
						</div>
						<div className="row no-margin mt-3">
							<div className="col-sm-12 padding-small">
								<CardsTable
									title="Carriera"
									fullHeight={false}
									middleHeight
									configurationColumns={careerColumns}
									data={playerData?.career}
									actions={[
										{
											icon: <AddIcon />,
											onClick: () => setOpenCareerDetail(true),
										},
									]}
									handleEdit={(item) => {
										setInitialData(Object.assign({}, item));
										setOpenCareerDetail(true);
									}}
									handleDelete={(item) => {
										deleteCareerDetail(item.id);
									}}
									defaultSortedField="dateEvent"
									defaultSortedOrder="descend"
								/>
							</div>
						</div>
						<div className="row no-margin mt-2">
							<div className="col-sm-12 padding-small">
								<CardsContainer
									tableComponent={
										<CardsTable
											title="Movimenti"
											fullWidth
											fullHeight={false}
											middleHeight
											configurationColumns={movementsColumns}
											data={playerMovements}
											actions={
												user?.userName !== "CaputoD"
													? [
															{
																icon: <AddIcon />,
																onClick: () => setOpenMovement(true),
															},
														]
													: []
											}
											rowActions={user?.userName !== "CaputoD"}
											handleEdit={(item) => {
												setInitialData(Object.assign({}, item));
												setOpenMovement(true);
											}}
											handleDelete={(item) => {
												deleteMovement(item.id);
											}}
											defaultSortedField="movementDate"
											defaultSortedOrder="descend"
										/>
									}
									listComponent={
										<CardsList
											type="player_movement"
											listConfiguration={careerConfiguration}
											defaultSortedField="movementDate"
											defaultSortedOrder="descend"
										/>
									}
								/>
							</div>
						</div>
						{/* <NotesSection
							type="player"
							notes={playerData?.playersNotes}
							handleConfirm={handleNote}
							handleDelete={deleteNote}
						/> */}
						{user?.userName !== "CaputoD" && (
							<div className="row no-margin mt-2">
								<div className="col-sm-12 padding-small mt-2">
									<VLFCard
										title={t("attachments")}
										content={
											<AttachmentsContainer
												entityId={id}
												entityType="player"
												attachments={attachments}
												handleAdd={() => getPlayerAttachments()}
												handleDownload={handleDownload}
												handleDelete={(id) => {
													setIdAttachment(id);
													setOpenHandleAttachmentConfirm(true);
												}}
												handleNotification={handleNotification}
											/>
										}
									/>
									<VLFModal
										open={openHandleAttachmentConfirm}
										title="Eliminazione"
										content={
											<div className="mt-2">
												Sei sicuro di voler eliminare questo elemento?
											</div>
										}
										confirm={() => deleteAttachment()}
										close={() => setOpenHandleAttachmentConfirm(false)}
										confirmLabel="Conferma"
									/>
								</div>
							</div>
						)}
						{openContact ? (
							<HandleContactModal
								open={openContact}
								confirm={handleContact}
								close={handleClose}
								initialData={initialData}
							/>
						) : null}
						{openAgents ? (
							<HandleAgentModal
								open={openAgents}
								confirm={handleAgent}
								close={handleClose}
								initialData={initialData}
							/>
						) : null}
						{openMovement ? (
							<HandleMovementModal
								open={openMovement}
								confirm={handleMovement}
								close={handleClose}
								initialData={initialData}
							/>
						) : null}
						<VLFModal
							open={openHandleConfirm}
							title="Eliminazione"
							content={
								<div className="mt-2">
									Sei sicuro di voler eliminare questo elemento?
								</div>
							}
							confirm={() => deletePlayer()}
							close={() => setOpenHandleConfirm(false)}
							confirmLabel="Conferma"
						/>
					</>
				);
			},
		},
		{
			key: "stats",
			label: t("stats"),
			hidden: user?.userName === "CaputoD",
			render: () => {
				return (
					<div className="row no-margin col-container">
						{playerData?.hasStats ? (
							<div className="col-12 padding-small">
								<VLFCard
									title="Statistiche partite"
									content={
										<div className="match-player-stats detailed">
											<div className="match-stats-details-container">
												{detailedQuickStats
													?.sort((a, b) => {
														const dateA = new Date(a.match.startDate);
														const dateB = new Date(b.match.startDate);
														return dateB - dateA;
													})
													?.map((dqs, k) => {
														return (
															<div key={k} className="d-flex flex-column w-100">
																<div className="f-bold">
																	{moment(dqs.match.startDate).format(
																		"DD/MM/YYYY",
																	) +
																		" " +
																		dqs.match?.homeClub +
																		" - " +
																		dqs.match?.awayClub +
																		" " +
																		(dqs.match.homeClubGoals
																			? dqs.match.homeClubGoals +
																				"-" +
																				dqs.match.awayClubGoals
																			: "---")}
																</div>
																<div className="match-stats detailed">
																	{getStatsRow(dqs)}
																</div>
															</div>
														);
													})}
											</div>
										</div>
									}
								/>
							</div>
						) : null}
						<div className="col-12 padding-small mt-2">
							<VLFCard
								title="Partite"
								content={
									<div className="row">
										{getChartFilters(
											matchesFilter,
											setMatchesFilter,
											matchesDate,
											setMatchesDate,
										)}
										<CardsContainer
											tableComponent={
												<CardsTable
													configurationColumns={playedMatchesColumns}
													data={playedMatches}
													fullWidth
													fullHeight={false}
													middleHeight
													handleClick={(item) => {
														navigate("/matches/" + item.matchId);
													}}
													defaultSortedField="matchDate"
													defaultSortedOrder="descend"
												/>
											}
											listComponent={
												<CardsList
													type="match_player"
													middleHeight
													listConfiguration={playedMatchesConfiguration}
													defaultSortedField="matchDate"
													defaultSortedOrder="descend"
												/>
											}
										/>
										{matchesFilter !== "monthly" &&
											Object.keys(matchesStats || {})
												.sort((a, b) => {
													const [yearA, monthA] = a.split("-");
													const [yearB, monthB] = b.split("-");
													const dateA = new Date(
														parseInt(yearA, 10),
														parseInt(monthA, 10) - 1,
													);
													const dateB = new Date(
														parseInt(yearB, 10),
														parseInt(monthB, 10) - 1,
													);
													return dateA - dateB;
												})
												?.map((mk, k) => {
													const fullWidthBox = matchesFilter === "monthly";
													const month = mk.split("-")[1] - 1;
													const year = mk.split("-")[0];

													return (
														<div
															className={
																fullWidthBox
																	? "col-12 padding-small mt-3"
																	: "col-12 col-xl-6 padding-small mt-3"
															}
															key={k}
														>
															<div
																className={"month-matches-container"}
																key={k}
															>
																<div className="month-name">
																	{fullWidthBox
																		? "Totale"
																		: moment()
																				.set({month, year})
																				.format("MMMM YYYY")}
																</div>
																<div className={"month-values-container " + mk}>
																	{Object.keys(matchesStats[mk]).map(
																		(mkt, k2) => {
																			return (
																				<div
																					className="match-type-container"
																					key={k2}
																				>
																					<div className="match-type">
																						{mkt}
																					</div>
																					{Object.keys(
																						matchesStats[mk][mkt],
																					).map((mkts, k3) => {
																						return (
																							<div
																								className="type-stats-container"
																								key={k3}
																							>
																								<div className="type-stat-value">
																									{matchesStats[mk][mkt][mkts]}
																								</div>
																								<div className="type-stat-label">
																									{MATCH_STATS[mkts]}
																								</div>
																							</div>
																						);
																					})}
																				</div>
																			);
																		},
																	)}
																</div>
															</div>
														</div>
													);
												})}
										{Object.keys(matchesStats)?.length ? (
											<div className="col-12 padding-small mt-2">
												<div className="month-matches-container">
													<div className="month-name">Totale</div>
													<div className="month-values-container all">
														{Object.keys(totalMatchesStats)?.map((ts, k) => {
															const current = totalMatchesStats[ts];
															return (
																<div key={k} className="match-type-container">
																	<div className="match-type">{ts}</div>
																	{playerData?.goalkeeper ? (
																		<div className="type-stats-container">
																			<div className="type-stat-value">
																				{(current.goalsConceded || "---") +
																					"/" +
																					(current.totalGoalsConceded ||
																						"---")}{" "}
																			</div>
																			<div className="type-stat-label">
																				Goal subiti
																			</div>
																		</div>
																	) : null}
																	<div className="type-stats-container">
																		<div className="type-stat-value">
																			{current.playedMinutes +
																				"/" +
																				current.totalPlayedMinutes}
																		</div>
																		<div className="type-stat-label">
																			Minuti
																		</div>
																	</div>
																	<div className="type-stats-container">
																		<div className="type-stat-value">
																			{current.convocations +
																				"/" +
																				current.matches}
																		</div>
																		<div className="type-stat-label">
																			Partite
																		</div>
																	</div>
																</div>
															);
														})}
													</div>
												</div>
											</div>
										) : null}
										{!Object.keys(matchesStats)?.length && <EmptyData />}
									</div>
								}
							/>
						</div>
						{playerData?.hasStats ? (
							<div className="col-12 padding-small mt-2">
								<VLFCard
									title="Allenamenti"
									content={
										<div className="row trainings-stats-row">
											<div className="col-sm-12">
												<div className="stat-options">
													<div className="stat-switch">
														<div
															className={
																"single-option clickable" +
																(trainingsFilter === "weekly"
																	? " selected"
																	: "")
															}
															onClick={() => {
																setTrainingsFilter("weekly");
															}}
														>
															Mese - settimana
														</div>
														<div
															className={
																"single-option clickable" +
																(trainingsFilter === "daily" ? " selected" : "")
															}
															onClick={() => {
																setTrainingsFilter("daily");
															}}
														>
															Settimana - giorno
														</div>
													</div>
													{trainingsFilter === "daily" && (
														<div className="stat-input mx-5">
															<VLFDatePicker
																type="week"
																customDateFormat="w/yyyy"
																value={trainingsDate}
																onChange={setTrainingsDate}
																required
																size="small"
															/>
														</div>
													)}
													{trainingsFilter === "weekly" && (
														<div className="stat-input mx-5">
															<VLFDatePicker
																type="month"
																customDateFormat="MM/yyyy"
																value={trainingsDate}
																onChange={setTrainingsDate}
																required
																size="small"
															/>
														</div>
													)}
												</div>
											</div>
											<CardsTable
												fullHeight={false}
												fullWidth
												configurationColumns={
													trainingsFilter === "daily"
														? trainingsDailyColumns
														: trainingsWeeklyColumns
												}
												data={trainingsStats}
												defaultSortedField={
													trainingsFilter === "daily"
														? "startDate"
														: "weekStartDate"
												}
												defaultSortedOrder="descend"
											/>
											<div className="col-12">
												<div className="trainings-stats-container">
													{trainingsStats.map((ts, k) => {
														return (
															<div key={k} className="table-element">
																<div className="element-main-info">
																	<div>
																		{trainingsFilter === "daily"
																			? moment(ts.trainingDate).format(
																					dateFormat,
																				) +
																				" " +
																				moment(
																					ts.trainingData?.startDate,
																				).format("HH:mm")
																			: moment(ts.weekStartDate).format(
																					dateFormat,
																				) +
																				" - " +
																				moment(ts.weekEndDate).format(
																					dateFormat,
																				)}
																	</div>
																</div>
																<div className="d-flex flex-column w-100">
																	{trainingsFilter === "daily" ? (
																		<div
																			className="d-flex align-items-center justify-content-end px-2"
																			style={{fontSize: "12px"}}
																		>
																			{ts.trainingData?.playersCategory}
																		</div>
																	) : (
																		""
																	)}
																	<div className="element-info-container d-flex align-items-center flex-wrap ">
																		{trainingsValues.map((tv, kv) => {
																			return (
																				<div
																					key={kv}
																					className="info-container d-flex flex-column align-items-center"
																				>
																					<div className="value">
																						{ts[tv.value]}
																					</div>
																					<div className="label">
																						{tv.label}
																					</div>
																				</div>
																			);
																		})}
																	</div>
																</div>
															</div>
														);
													})}
													{!trainingsStats.length && <EmptyData />}
												</div>
											</div>
										</div>
									}
								/>
							</div>
						) : null}
					</div>
				);
			},
		},
		{
			key: "performance",
			label: "Performance",
			hidden: user?.userName === "CaputoD",
			render: () => {
				return (
					<div className="row no-margin col-container">
						<PlayerStatsContainer type="rpe" player={playerData} />
						<PlayerStatsContainer type="tqr" player={playerData} />
						{playerData?.hasStats ? (
							<div className="col-12 padding-small mt-2">
								<VLFCard
									title="Recuperi"
									content={
										<div className="row ">
											{getChartFilters(
												tqrFilter,
												setTQRFilter,
												tqrDate,
												setTQRDate,
											)}
											{getChartStats(tqrStats, "avgTqr", tqrFilter)}
											<div className="col-sm-12 mt-2">
												<ChartContainer
													type="line"
													chartItems={[
														{
															value: "minTqr",
															label: "Minimo",
															color: "#007BFF",
														},
														{
															value: "avgTqr",
															label: "Medio",
															color: "#FFA500",
														},
														{
															value: "maxTqr",
															label: "Massimo",
															color: "#28A745",
														},
													]}
													data={tqrStats.map((ts) => {
														const date = moment().set({
															year: ts.year,
															month: ts.month - 1,
														});
														return {
															...ts,
															chartLabel:
																tqrFilter === "monthly"
																	? "Settimana " + ts.weekNumber
																	: date.format("MMMM YYYY"),
														};
													})}
													dataKey="chartLabel"
												/>
											</div>
										</div>
									}
								/>
							</div>
						) : null}
						{playerData?.hasStats ? (
							<div className="col-12 padding-small mt-2">
								<VLFCard
									title="RPE"
									content={
										<div className="row">
											{getChartFilters(
												rpeFilter,
												setRPEFilter,
												rpeDate,
												setRPEDate,
											)}
											{getChartStats(rpeStats, "avgRpe", rpeFilter)}
											<div className="col-sm-12 mt-2">
												<ChartContainer
													type="line"
													chartItems={[
														{
															value: "minRpe",
															label: "Minimo",
															color: "#007BFF",
														},
														{
															value: "avgRpe",
															label: "Medio",
															color: "#FFA500",
														},
														{
															value: "maxRpe",
															label: "Massimo",
															color: "#28A745",
														},
													]}
													data={rpeStats.map((rs) => {
														const date = moment().set({
															year: rs.year,
															month: rs.month - 1,
														});
														return {
															...rs,
															chartLabel:
																rpeFilter === "monthly"
																	? "Settimana " + rs.weekNumber
																	: date.format("MMMM YYYY"),
														};
													})}
													dataKey="chartLabel"
												/>
											</div>
										</div>
									}
								/>
							</div>
						) : null}
						{playerData?.hasStats ? (
							<>
								<div className="col-sm-12 padding-small">
									<div
										className="d-flex align-items-center justify-content-between mt-3 pt-2"
										style={{borderTop: "1px solid #dad9d9"}}
									>
										<div className="f-bold" style={{fontSize: "16px"}}>
											Test atletici
										</div>
										<Button
											variant="outlined"
											onClick={() => {
												navigate("/athletic-tests/new?playerId=" + id);
											}}
											size="small"
										>
											<div className="d-flex align-items-center">
												<AddIcon />
											</div>
										</Button>
									</div>
								</div>
								<div className="col-sm-12 padding-small">
									<CardsContainer
										tableComponent={
											<div className="d-flex flex-column gap-2">
												{playerData?.goalkeeper ? (
													<CardsTable
														title="Corse"
														size="sm"
														configurationColumns={runningColumns.sort(
															(a, b) => {
																const numA = parseInt(a.label);
																const numB = parseInt(b.label);
																return numA - numB;
															},
														)}
														data={runningData}
														handleClick={(test) =>
															navigate("/athletic-tests/" + test.testId)
														}
														fullWidth
														fullHeight={false}
														defaultSortedField={"testDate"}
														defaultSortedOrder="descend"
													/>
												) : null}
												{playerData?.goalkeeper ? (
													<CardsTable
														title="Spinte"
														size="sm"
														configurationColumns={jumpingColumns}
														data={jumpingData}
														handleClick={(test) =>
															navigate("/athletic-tests/" + test.testId)
														}
														fullWidth
														fullHeight={false}
														defaultSortedField={"testDate"}
														defaultSortedOrder="descend"
													/>
												) : null}
												{!playerData?.goalkeeper ? (
													<CardsTable
														title="Test di resistenza"
														size="sm"
														configurationColumns={resistenceColumns}
														data={resistenceData}
														handleClick={(test) =>
															navigate("/athletic-tests/" + test.testId)
														}
														fullWidth
														fullHeight={false}
														defaultSortedField={"testDate"}
														defaultSortedOrder="descend"
													/>
												) : null}
												{!playerData?.goalkeeper ? (
													<CardsTable
														title="Test di agilità"
														size="sm"
														configurationColumns={agilityColumns}
														data={agilityData}
														handleClick={(test) =>
															navigate("/athletic-tests/" + test.testId)
														}
														fullWidth
														fullHeight={false}
														defaultSortedField={"testDate"}
														defaultSortedOrder="descend"
													/>
												) : null}
												{!playerData?.goalkeeper ? (
													<CardsTable
														title="Test di forza esplosiva"
														size="sm"
														configurationColumns={strengthColumns}
														data={strengthData}
														handleClick={(test) =>
															navigate("/athletic-tests/" + test.testId)
														}
														fullWidth
														fullHeight={false}
														defaultSortedField={"testDate"}
														defaultSortedOrder="descend"
													/>
												) : null}
											</div>
										}
										listComponent={
											<CardsList
												type="test_base"
												breadcrumbs
												grouped
												tabbed
												listConfiguration={listConfiguration}
												defaultGroupSortedOrder="ascend"
												defaultSortedField={"testDate"}
												defaultSortedOrder="descend"
											/>
										}
									/>
								</div>
							</>
						) : null}
						{playerData?.hasStats ? (
							<>
								<div className="col-sm-12 padding-small">
									<div
										className="d-flex align-items-center justify-content-between pt-2 mt-3"
										style={{borderTop: "1px solid #dad9d9"}}
									>
										<div className="f-bold" style={{fontSize: "16px"}}>
											GPS
										</div>
									</div>
								</div>
								<div className="col-sm-12 padding-small">
									<GPSContainer
										handleNotification={handleNotification}
										gpsData={playerGPSData}
										playerId={id}
										callback={getPlayerGPS}
									/>
								</div>
							</>
						) : null}
					</div>
				);
			},
		},
		{
			key: "athletic_data",
			label: t("anthropometric_data"),
			hidden: user?.userName === "CaputoD",
			render: () => {
				return (
					<div>
						<div className="row no-margin col-container">
							<div className="col-12 col-lg-6 padding-small">
								<CardsTable
									fullHeight={false}
									middleHeight
									configurationColumns={physicalHeightColumns}
									data={playerData?.playersGrowth?.filter(
										(elem) => elem.height,
									)}
									defaultSortedField="date"
									defaultSortedOrder="descend"
									showModes
									chartData={{
										type: "line",
										title: "Altezza",
										value: "height",
										label: "Altezza",
									}}
									actions={
										user?.userName !== "CaputoD"
											? [
													{
														icon: <AddIcon />,
														onClick: () => setOpenPhysicHeightInfo(true),
													},
												]
											: []
									}
									rowActions={user?.userName !== "CaputoD"}
									handleEdit={(item) => {
										setInitialData(Object.assign({}, item));
										setOpenPhysicHeightInfo(true);
									}}
									handleDelete={(item) => {
										deletePhysicInfo(item.id);
									}}
								/>
							</div>
							<div className="col-12 col-lg-6 padding-small">
								<CardsTable
									fullHeight={false}
									middleHeight
									configurationColumns={physicalWeightColumns}
									data={playerData?.playersGrowth?.filter(
										(elem) => elem.weight,
									)}
									defaultSortedField="date"
									defaultSortedOrder="descend"
									showModes
									chartData={{
										type: "bar",
										title: "Peso",
										value: "weight",
										label: "Peso",
									}}
									actions={
										user?.userName !== "CaputoD"
											? [
													{
														icon: <AddIcon />,
														onClick: () => setOpenPhysicInfoWeight(true),
													},
												]
											: []
									}
									rowActions={user?.userName !== "CaputoD"}
									handleEdit={(item) => {
										setInitialData(Object.assign({}, item));
										setOpenPhysicInfoWeight(true);
									}}
									handleDelete={(item) => {
										deletePhysicInfo(item.id);
									}}
								/>
							</div>
							<div className="col-12 padding-small">
								<div className="px-1 mt-3 f-bold" style={{fontSize: "16px"}}>
									Altre misurazioni
								</div>
							</div>
							<div className="col-12 padding-small">
								<CardsTable
									fullHeight={false}
									middleHeight
									configurationColumns={measurementsColumns}
									data={playerData?.playersGrowth?.filter(
										(pg) => pg.measurementType === 1,
									)}
									defaultSortedField="date"
									defaultSortedOrder="descend"
									actions={
										user?.userName !== "CaputoD"
											? [
													{
														icon: <AddIcon />,
														onClick: () => setOpenMeasurementInfo(true),
													},
												]
											: []
									}
									rowActions={user?.userName !== "CaputoD"}
									handleEdit={(item) => {
										setInitialData(Object.assign({}, item));
										setOpenMeasurementInfo(true);
									}}
									handleDelete={(item) => {
										deletePhysicInfo(item.id);
									}}
								/>
							</div>
							<div className="col-12 padding-small">
								<div className="px-1 mt-3 f-bold" style={{fontSize: "16px"}}>
									PHV
								</div>
							</div>
							<div className="col-12 padding-small">
								<CardsTable
									fullHeight={false}
									middleHeight
									configurationColumns={phvColumns}
									data={playerData?.playersGrowth?.filter(
										(pg) => pg.measurementType === 2,
									)}
									defaultSortedField="date"
									defaultSortedOrder="descend"
									actions={
										user?.userName !== "CaputoD"
											? [
													{
														icon: <AddIcon />,
														onClick: () => setOpenPHVInfo(true),
													},
												]
											: []
									}
									rowActions={user?.userName !== "CaputoD"}
									handleEdit={(item) => {
										setInitialData(Object.assign({}, item));
										setOpenPHVInfo(true);
									}}
									handleDelete={(item) => {
										deletePhysicInfo(item.id);
									}}
								/>
							</div>
						</div>
						{openPhysicHeightInfo ? (
							<HandlePhysicHeightInfo
								open={openPhysicHeightInfo}
								confirm={(data, id) =>
									handlePhysicInfo({...data, measurementType: 0}, id)
								}
								close={handleClose}
								initialData={initialData}
							/>
						) : null}
						{openPhysicInfoWeight ? (
							<HandlePhysicWeightInfo
								open={openPhysicInfoWeight}
								confirm={(data, id) =>
									handlePhysicInfo({...data, measurementType: 0}, id)
								}
								close={handleClose}
								initialData={initialData}
							/>
						) : null}
						{openMeasurementInfo ? (
							<HandleMeasurementInfo
								open={openMeasurementInfo}
								confirm={(data, id) =>
									handlePhysicInfo({...data, measurementType: 1}, id)
								}
								close={handleClose}
								initialData={initialData}
							/>
						) : null}
						{openPHVInfo ? (
							<HandlePHVInfo
								open={openPHVInfo}
								confirm={(data, id) =>
									handlePhysicInfo({...data, measurementType: 2}, id)
								}
								close={handleClose}
								initialData={initialData}
							/>
						) : null}
					</div>
				);
			},
		},
		{
			key: "valuations",
			label: t("valuations"),
			hidden: user?.userName === "CaputoD",
			render: () => {
				return currentTab === "valuations" ? (
					<div className="row no-margin g-2">
						<div className="col-sm-12 padding-small">
							<VLFCard
								openable={false}
								content={
									<div className="row no-margin g-1">
										<div className="col-12 col-md-4 padding-small">
											<div className="kpi-box-container">
												<div className="kpi-label">Rating giocatore</div>
												<div className="kpi-value">
													{playerRating !== null
														? Number(playerRating.toFixed(2))
														: "---"}
												</div>
											</div>
										</div>
										<div className="col-12 col-md-4 padding-small">
											<div className="kpi-box-container">
												<div className="kpi-label">Rating gara</div>
												<div className="kpi-value">
													{matchReportsStats.average !== null
														? Number(matchReportsStats.average.toFixed(2))
														: "---"}
												</div>
											</div>
										</div>
										<div className="col-12 col-md-4 padding-small">
											<div className="kpi-box-container">
												<div className="kpi-label">N° partite con report</div>
												<div className="kpi-value">
													{matchReportsStats.count || 0}
												</div>
											</div>
										</div>
									</div>
								}
							/>
						</div>
						<div className="col-sm-12 padding-small">
							<VLFCard
								title="Andamento report gara"
								openable={false}
								content={
									<div className="row no-margin">
										<div className="col-12">
											<ChartContainer
												type="line"
												chartItems={[
													{
														value: "rating",
														label: "Rating gara",
														color: "#16a34a",
													},
												]}
												data={matchReportsStats.trend}
												dataKey="date"
											/>
										</div>
									</div>
								}
							/>
						</div>
						<div
							className="px-1"
							style={{
								fontSize: "16px",
								fontWeight: "bold",
								marginBottom: "-10px",
							}}
						>
							Valutazioni generali
						</div>
						<ValuationsPage playerId={id} />
						{playerData?.goalkeeper && (
							<div
								className="px-1 mt-3"
								style={{
									fontSize: "16px",
									fontWeight: "bold",
									marginBottom: "-10px",
								}}
							>
								Report gara
							</div>
						)}
						{playerData?.goalkeeper && <MatchReportsPage playerId={id} />}
						{/* <NotesSection
							type="player_match"
							notes={playerNotes}
							chargeData={false}
						/> */}
					</div>
				) : (
					<></>
				);
			},
		},
		{
			key: "medical",
			label: t("medical_passport"),
			hidden: user?.userName === "CaputoD",
			render: () => {
				return (
					<MedicalRecordPage
						onlyDetails
						handleNotification={handleNotification}
					/>
				);
			},
		},
		{
			key: "video",
			label: t("video"),
			hidden: user?.userName === "CaputoD",
			render: () => {
				return (
					<AttachmentsContainer attachments={playlists} editable={false} />
				);
			},
		},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					playerData
						? (playerData.firstName || "") + " " + (playerData.lastName || "")
						: ""
				}
				actions={<MainActions actions={mainActions} />}
			/>
			<Tabs
				tabs={tabs}
				currentTab={currentTab}
				setCurrentTab={(tab) => dispatch(setPlayerTab(tab))}
			/>
			{openCareerDetail ? (
				<HandleCareerModal
					open={openCareerDetail}
					confirm={handleCareerDetail}
					close={() => {
						setOpenCareerDetail(false);
						setInitialData();
					}}
					initialData={initialData}
					handleNotification={handleNotification}
				/>
			) : null}
		</Page>
	);
}

export default PlayerPage;
